import { Component, effect, viewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { ServiceOperationalReportsPageStore } from './service-operational-reports-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { fb, routeParams, translate } from '../../../../shared/shared.global-service';
import { TableColumn } from '../../../../shared/components/kit/table/table.types';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { ServiceSettingsUpdateInput } from '@libs/types/src/services/input-type/services/service-settings.update-input';
import {
    SERVICE_OPERATIONAL_REPORTS_KEY,
    ServiceSettingsOperationalReportType,
} from '@libs/types/src/services/types/service-settings-operational-report.type';
import { FilterBuilder } from '../../../helper/filter.builder';
import { FormArray, Validators } from '@angular/forms';
import {
    ServiceSettingsShiftsDialogComponent,
} from '../../../components/services/dialogs/service-settings-shifts-dialog/service-settings-shifts-dialog.component';
import { contentGlobalSuccess } from '../../../content.error';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { getOperationalReportTypeFrag } from './service-operational-reports-page.query';
import {
    OperationalReportTypeObject,
} from '@libs/types/src/services/object-type/service/operational-report-type.object';
import {
    OperationalReportTypeUpsertModelComponent,
} from './operational-report-type-upsert-model/operational-report-type-upsert-model.component';
import {
    ServiceSettingsCommonComponent,
} from '../../../components/services/settings/service-settings-common/service-settings-common.component';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { Divider } from '@openng/optimus-ui/divider';
import { CheckInputComponent } from '../../../../../components/kit/input/check-input/check-input.component';
import { LinkButtonComponent } from '../../../../../components/kit/button/link-button.component';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { CardTableComponent } from '../../../../shared/components/kit/table/card-table.component';

@Component({
    selector: 'app-service-operational-reports-page',
    templateUrl: './service-operational-reports-page.component.html',
    styleUrl: './service-operational-reports-page.component.scss',
    providers: [ServiceOperationalReportsPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ServiceSettingsCommonComponent,
        Divider,
        CheckInputComponent,
        LinkButtonComponent,
        ActionButtonsComponent,
        CardTableComponent,
    ],
})
export class ServiceOperationalReportsPageComponent extends SubscriptionComponent {

    protected readonly store = inject(ServiceOperationalReportsPageStore);
    private readonly dialogService = inject(BaseDialogService);
    private readonly confirm = inject(ConfirmBuilder);

    public readonly typeTable = viewChild<TableComponent>('typeTable');
    public readonly serviceSettingsCommonComponent = viewChild(ServiceSettingsCommonComponent);


    protected typeColumns: TableColumn[] = [
        { name: 'key', filterable: true, sortable: true },
    ];

    protected ssForm = fb.group({
        useLocation: fb.control<boolean>(false),
        useShift: fb.control<boolean>(false),
        useForm: fb.control<boolean>(false),
        shifts: fb.array<string>(['Ranní', 'Noční'], Validators.required),
    });

    private operationalSettings: ServiceSettingsOperationalReportType | null = null;

    constructor() {
        super();

        effect(() => {
            this.store.api.getOperationalReportTypes(FilterBuilder.filter({
                serviceId: routeParams.serviceId(),
            }), getOperationalReportTypeFrag);
        });


        effect(() => {
            const settings = this.store.operationalSettings();
            this.operationalSettings = settings;

            this.ssForm.patchValue({
                useLocation: settings?.useLocation || false,
                useShift: settings?.useShift || false,
                useForm: settings?.useForm || false,
            });
            const shiftsArray = this.ssForm.get('shifts') as FormArray;
            shiftsArray.clear();
            settings?.shifts?.forEach((shift: string) => {
                const control = fb.control(shift, [Validators.required]);
                shiftsArray.push(control);
            });
        });
    }

    protected upsertType(type?: OperationalReportTypeObject): void {
        this.dialogService.open(
            OperationalReportTypeUpsertModelComponent,
            {
                header: translate.instant('service.operationalReport.fields.type'),
                styleClass: 'p-dialog-width-50',
                data: { type },
            },
        ).onClose.subscribe((it) => {
            it && this.typeTable()?.reload();
        });
    }

    protected async deleteType(event: any): Promise<void> {
        this.confirm.confirmDelete(
            async () => {
                await this.store.api.deleteOperationalReportType({ id: event.item.id });
                contentGlobalSuccess.next('common.deleted');
                this.typeTable()?.reload();
            },
            event,
        );
    }

    openShiftsDialog() {
        const ref = this.dialogService.open(ServiceSettingsShiftsDialogComponent, {
            header: translate.instant('service.operationalReport.settings.editShifts'),
            data: {
                shifts: this.ssForm.get('shifts') as FormArray,
            },
        });

        ref.onClose.subscribe((result: string[]) => {
            if (result) {
                const shiftsArray = this.ssForm.get('shifts') as FormArray;
                shiftsArray.setValue(result);

                this.ssForm.markAsDirty();
                this.ssForm.updateValueAndValidity();
                this.saveSettings().then();
            }
        });
    }

    protected async saveSettings(): Promise<void> {
        const serviceId = routeParams.serviceId();
        if (!serviceId) return;

        const data = {
            ...JSON.parse(this.serviceSettingsCommonComponent()?.getFormData() || '{}'),
            ...this.ssForm.getRawValue(),
        };

        const settings: ServiceSettingsUpdateInput = {
            key: SERVICE_OPERATIONAL_REPORTS_KEY,
            data: JSON.stringify(data),
            serviceId: Number(routeParams.serviceId()),
        };
        await this.store.api.updateServiceSettings(settings);
        contentGlobalSuccess.next('common.updated');
    }

    protected reset(): void {
        if (this.operationalSettings) {
            this.ssForm.reset(this.operationalSettings);
        }
    }

    protected readonly routeParams = routeParams;
    protected readonly getOperationalReportTypeFrag = getOperationalReportTypeFrag;
}
